import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function DatePicker({ value, onChange, testId = "picker-date", placeholder = "Select date", allowPast = false }) {
  const [open, setOpen] = useState(false);
  const dateObj = value ? new Date(value + "T00:00:00") : undefined;

  const handleSelect = (d) => {
    if (!d) return;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="lux-input flex items-center justify-between gap-3 text-left w-full"
          data-testid={testId}
        >
          <span className={dateObj ? "text-white" : "text-white/40"}>
            {dateObj ? format(dateObj, "EEE, MMM d, yyyy") : placeholder}
          </span>
          <CalendarIcon size={16} className="text-[#d4af6a] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto p-0 bg-[#0c0c0c] border-white/10 text-white shadow-2xl"
      >
        <Calendar
          mode="single"
          selected={dateObj}
          onSelect={handleSelect}
          disabled={(d) => {
            if (allowPast) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return d < today;
          }}
          classNames={{
            months: "p-3",
            month: "space-y-3",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "font-medium text-white text-sm tracking-wide",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-transparent hover:bg-white/10 rounded-sm text-white/80 flex items-center justify-center",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "text-white/40 font-normal text-[10px] tracking-widest uppercase w-9",
            row: "flex w-full mt-1",
            cell: "h-9 w-9 text-center text-sm relative",
            day: "h-9 w-9 p-0 font-normal text-white/85 hover:bg-white/10 rounded-sm transition-colors",
            day_selected: "bg-[#9B1B30] text-white hover:bg-[#9B1B30] hover:text-white",
            day_today: "border border-[#d4af6a]/60",
            day_outside: "text-white/25",
            day_disabled: "text-white/15 cursor-not-allowed opacity-50",
          }}
        />
        <div className="border-t border-white/10 px-3 py-2 text-[10px] tracking-widest uppercase text-[#d4af6a]">
          Today? Call dispatch · 202-232-4440
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TimePicker({ value, onChange, testId = "picker-time", placeholder = "Select time" }) {
  const [open, setOpen] = useState(false);

  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  const fmt = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hr = parseInt(h, 10);
    const period = hr >= 12 ? "PM" : "AM";
    const hr12 = hr % 12 === 0 ? 12 : hr % 12;
    return `${hr12}:${m} ${period}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="lux-input flex items-center justify-between gap-3 text-left w-full"
          data-testid={testId}
        >
          <span className={value ? "text-white" : "text-white/40"}>
            {value ? fmt(value) : placeholder}
          </span>
          <Clock size={16} className="text-[#d4af6a] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-44 p-0 bg-[#0c0c0c] border-white/10 shadow-2xl"
      >
        <div className="max-h-72 overflow-y-auto py-1">
          {slots.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                value === s
                  ? "bg-[#9B1B30] text-white"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              }`}
              data-testid={`time-slot-${s}`}
            >
              {fmt(s)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
